import { Link } from "react-router-dom";
import type { Article } from "@/mocks/articles";

interface ArticleCardProps {
  article: Article;
  variant?: "default" | "horizontal" | "featured";
}

export default function ArticleCard({ article, variant = "default" }: ArticleCardProps) {
  const formatViews = (views: number) => {
    if (views >= 10000) {
      return `${(views / 10000).toFixed(1)}万`;
    }
    return views.toString();
  };

  if (variant === "featured") {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group block relative overflow-hidden rounded-lg cursor-pointer"
      >
        <div className="aspect-[16/9] relative">
          <img
            src={article.imageUrl}
            alt={article.title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background-950 via-background-950/40 to-transparent"></div>
          <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6">
            <span className="inline-block bg-primary-500 text-background-950 text-xs font-bold px-2 py-1 rounded whitespace-nowrap mb-3">
              {article.tags[0]}
            </span>
            <h3 className="text-lg md:text-xl font-heading font-bold text-foreground-50 group-hover:text-primary-300 transition-colors duration-200 line-clamp-2">
              {article.title}
            </h3>
            <p className="mt-2 text-sm text-foreground-400 line-clamp-2 hidden sm:block">{article.excerpt}</p>
            <div className="mt-3 flex items-center gap-3 text-xs text-foreground-500">
              <span>{article.publishDate}</span>
              <span className="w-1 h-1 bg-foreground-600 rounded-full"></span>
              <span>{formatViews(article.views)} 阅读</span>
            </div>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === "horizontal") {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group flex gap-4 p-3 rounded-lg hover:bg-background-800/50 transition-colors duration-200 cursor-pointer"
      >
        <div className="w-28 h-20 sm:w-36 sm:h-24 flex-shrink-0 rounded-md overflow-hidden">
          <img
            src={article.imageUrl}
            alt={article.title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-heading font-semibold text-foreground-100 group-hover:text-primary-400 transition-colors duration-200 line-clamp-2">
            {article.title}
          </h3>
          <p className="mt-1 text-xs text-foreground-500 line-clamp-2 hidden sm:block">{article.excerpt}</p>
          <div className="mt-2 flex items-center gap-2 text-xs text-foreground-600">
            <span>{article.publishDate}</span>
            <span className="w-1 h-1 bg-foreground-700 rounded-full"></span>
            <span>{formatViews(article.views)} 阅读</span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.id}`}
      className="group block rounded-lg overflow-hidden bg-background-900 hover:bg-background-800/60 transition-colors duration-200 cursor-pointer"
    >
      <div className="aspect-[16/10] overflow-hidden">
        <img
          src={article.imageUrl}
          alt={article.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
      </div>
      <div className="p-4">
        <div className="flex items-center gap-2 flex-wrap mb-2">
          <span className="text-xs text-accent-500 bg-accent-500/10 px-2 py-0.5 rounded-full whitespace-nowrap">
            {article.tags[0]}
          </span>
          {article.isHot && (
            <span className="text-xs text-primary-400 bg-primary-500/10 px-2 py-0.5 rounded-full whitespace-nowrap flex items-center gap-1">
              <i className="ri-fire-line text-[10px]"></i>热门
            </span>
          )}
        </div>
        <h3 className="text-sm md:text-base font-heading font-semibold text-foreground-100 group-hover:text-primary-400 transition-colors duration-200 line-clamp-2">
          {article.title}
        </h3>
        <p className="mt-2 text-xs text-foreground-500 line-clamp-2">{article.excerpt}</p>
        <div className="mt-3 flex items-center justify-between">
          <div className="flex items-center gap-2 text-xs text-foreground-600">
            <span>{article.publishDate}</span>
            <span className="w-1 h-1 bg-foreground-700 rounded-full"></span>
            <span>{formatViews(article.views)} 阅读</span>
          </div>
        </div>
      </div>
    </Link>
  );
}